import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CognitoAuthProvider, useCognitoAuth } from './contexts/CognitoAuthContext';
import { AppProvider, useApp } from './contexts/AppContext';
import './styles/leadCategoryColors.css';
import MainLayout from './components/Layout/MainLayout';
import MainPage from './components/MainPage';
import Leads from './components/Leads';
// import Workspace from './components/Workspace';
import Teams from './components/Teams';
import Import from './components/Import';
import Notifications from './components/Notifications';
import Users from './components/Users';
import Settings from './components/Settings';
import Solutio from './components/Solutio';
import CognitoLogin from './components/CognitoLogin';
import Calendar from './components/Calendar';
import ClientManagement from './components/ClientManagement';
import ClientWorkspace from './components/ClientWorkspace';
import CoberturaFidelizacao from './components/CoberturaFidelizacao';
import TeamValidationProcessing from './components/TeamValidationProcessing';
import Simuladores from './components/Simuladores';
import TaskMaster from './components/TaskMaster';
import MEOPortalModal from './components/MEOPortalModal';
import AlticeResidencialForm from './components/AlticeResidencialForm';
import NotificationManager from './components/NotificationManager';
import SalesManager from './components/SalesManager';
import FilesProcessing from './components/FilesProcessing';
import { canAccessSimuladores, isAdmin, canAccessAppPage } from './utils/roleUtils';
import { getUserRole } from './utils/cognitoUtils';
// Partnership components removed - using main components with tenant-aware APIs
import SPARedirectHandler from './components/SPARedirectHandler';
import { getUserTenant, getMainPageUrl, isPathForTenant } from './utils/tenantUtils';
// import LeadWork from './components/LeadWork';
import './App.css';
import 'antd/dist/antd.css';

// NotFound component for handling missing routes
const NotFound = () => {
  const { isAuthenticated, isLoading } = useCognitoAuth();
  
  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f0f2f5'
      }}>
        <div style={{ textAlign: 'center' }}>Carregando...</div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If authenticated but on a not found page, redirect to main page
  const userTenant = getUserTenant();
  const mainPageUrl = getMainPageUrl(userTenant);
  return <Navigate to={mainPageUrl} replace />;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useCognitoAuth();
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Carregando...</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const TenantProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useCognitoAuth();
  const location = useLocation();
  
  // Don't redirect if already on login page
  if (location.pathname === '/login') {
    return <>{children}</>;
  }
  
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Carregando...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user has access to the current tenant area
  const userTenant = getUserTenant();
  const currentPath = location.pathname;
  
  if (!isPathForTenant(currentPath, userTenant)) {
    // Redirect to user's appropriate main page
    const mainPageUrl = getMainPageUrl(userTenant);
    return <Navigate to={mainPageUrl} replace />;
  }
  
  return <>{children}</>;
};

const SimuladoresProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useCognitoAuth();
  const { currentBusinessArea } = useApp();
  
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Carregando...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user has access to Simuladores
  if (!user || !user.role || !user.business_area || !canAccessSimuladores(user.role, user.business_area, currentBusinessArea)) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

const SalesManagerProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useCognitoAuth();
  
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Carregando...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is admin
  if (!user || !user.role || !isAdmin(getUserRole(user))) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

const AdminOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useCognitoAuth();
  const location = useLocation();

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Carregando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !canAccessAppPage(getUserRole(user), location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const [meoPortalVisible, setMeoPortalVisible] = useState(false);

  const handleMeoPortalClick = () => {
    setMeoPortalVisible(true);
  };

  const handleMeoPortalClose = () => {
    setMeoPortalVisible(false);
  };

  return (
    <CognitoAuthProvider>
      <AppProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <SPARedirectHandler />
          <Routes>
            <Route path="/login" element={<CognitoLogin />} />
            
            {/* All Routes - Tenant-aware (database separation handles security) */}
            <Route
              path="/*"
              element={
                <TenantProtectedRoute>
                  <NotificationManager>
                    <MainLayout>
                      <Routes>
                        <Route path="/" element={<MainPage />} />
                        <Route path="/client-workspace" element={<ClientWorkspace />} />
                        <Route path="/calendar" element={<AdminOnlyRoute><Calendar /></AdminOnlyRoute>} />
                        <Route path="/leads" element={<AdminOnlyRoute><Leads /></AdminOnlyRoute>} />
                        <Route path="/client-management" element={<AdminOnlyRoute><ClientManagement /></AdminOnlyRoute>} />
                        <Route path="/cobertura-fidelizacao" element={<AdminOnlyRoute><CoberturaFidelizacao /></AdminOnlyRoute>} />
                        <Route path="/team-validation" element={<AdminOnlyRoute><TeamValidationProcessing /></AdminOnlyRoute>} />
                        <Route path="/simuladores" element={<AdminOnlyRoute><SimuladoresProtectedRoute><Simuladores /></SimuladoresProtectedRoute></AdminOnlyRoute>} />
                        <Route path="/taskmaster" element={<AdminOnlyRoute><TaskMaster /></AdminOnlyRoute>} />
                        <Route path="/altice-residencial" element={<AdminOnlyRoute><AlticeResidencialForm /></AdminOnlyRoute>} />
                        <Route path="/teams" element={<AdminOnlyRoute><Teams /></AdminOnlyRoute>} />
                        <Route path="/import" element={<AdminOnlyRoute><Import /></AdminOnlyRoute>} />
                        <Route path="/notifications" element={<AdminOnlyRoute><Notifications /></AdminOnlyRoute>} />
                        <Route path="/settings" element={<AdminOnlyRoute><Settings /></AdminOnlyRoute>} />
                        <Route path="/users" element={<AdminOnlyRoute><Users /></AdminOnlyRoute>} />
                        <Route path="/solutio" element={<AdminOnlyRoute><Solutio /></AdminOnlyRoute>} />
                        <Route path="/sales-manager" element={<AdminOnlyRoute><SalesManagerProtectedRoute><SalesManager /></SalesManagerProtectedRoute></AdminOnlyRoute>} />
                        <Route path="/files-processing" element={<AdminOnlyRoute><SalesManagerProtectedRoute><FilesProcessing /></SalesManagerProtectedRoute></AdminOnlyRoute>} />
                        {/* Catch-all route for any unmatched paths */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </MainLayout>
                    <MEOPortalModal 
                      open={meoPortalVisible} 
                      onClose={handleMeoPortalClose} 
                    />
                  </NotificationManager>
                </TenantProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AppProvider>
    </CognitoAuthProvider>
  );
}

export default App;
